import {
    CrownOutlined,
    DeleteOutlined,
    EditOutlined,
    InfoCircleOutlined,
    LockOutlined,
    MailOutlined,
    SettingOutlined,
    UserOutlined,
} from '@ant-design/icons';
import { Button, Card, Divider, Form, Input, Modal, Spin, Switch, Tag } from 'antd';
import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { useAuth } from '../api/AuthContext.js';
import { changePassword, updateProfile } from '../api/auth.js';
import { getSubscriptionStatus } from '../api/subscriptions.js';
import { showToast } from '../utils/toastUtils.js';

function Settings() {
    const auth = useAuth();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [subscriptionData, setSubscriptionData] = useState(null);
    const [deleteModalVisible, setDeleteModalVisible] = useState(false);
    const [passwordModalVisible, setPasswordModalVisible] = useState(false);
    const [passwordForm] = Form.useForm();
    const [changingPassword, setChangingPassword] = useState(false);

    // Profile editing state
    const [isEditingProfile, setIsEditingProfile] = useState(false);
    const [profileForm] = Form.useForm();
    const [savingProfile, setSavingProfile] = useState(false);

    // Get user info from localStorage (set by AuthContext)
    const userName = localStorage.getItem('name') || 'User';
    const userEmail = localStorage.getItem('email') || '';

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        try {
            setLoading(true);
            const data = await getSubscriptionStatus();
            setSubscriptionData(data);
        } catch (err) {
            console.error('Error loading settings data:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleChangePassword = async (values) => {
        try {
            setChangingPassword(true);
            await changePassword(values.currentPassword, values.newPassword);
            showToast('Password changed successfully!', 'success');
            setPasswordModalVisible(false);
            passwordForm.resetFields();
        } catch (err) {
            const errorMessage =
                err.response?.data?.message || 'Failed to change password';
            showToast(errorMessage, 'error');
        } finally {
            setChangingPassword(false);
        }
    };

    const openPasswordModal = () => {
        setPasswordModalVisible(true);
        passwordForm.resetFields();
    };

    const handleEditProfile = () => {
        setIsEditingProfile(true);
        profileForm.setFieldsValue({
            name: userName,
            email: userEmail,
        });
    };

    const handleCancelEdit = () => {
        setIsEditingProfile(false);
        profileForm.resetFields();
    };

    const handleSaveProfile = async (values) => {
        try {
            setSavingProfile(true);
            const response = await updateProfile(values.name, values.email);

            // Update localStorage with new values
            localStorage.setItem('name', response.data.user.name);
            localStorage.setItem('email', response.data.user.email);

            // If email changed, the backend returns a new token
            // The AuthContext will handle the token update via the cookie

            showToast('Profile updated successfully!', 'success');
            setIsEditingProfile(false);

            // Reload the page to reflect changes
            window.location.reload();
        } catch (err) {
            const errorMessage =
                err.response?.data?.message || 'Failed to update profile';
            showToast(errorMessage, 'error');
        } finally {
            setSavingProfile(false);
        }
    };

    const handleAgeGroupsToggle = async (checked) => {
        try {
            await auth.updateAgeGroupsPreference(checked);
            showToast(
                `Age groups ${checked ? 'enabled' : 'disabled'} successfully!`,
                'success'
            );
        } catch (err) {
            const errorMessage =
                err.response?.data?.message ||
                'Failed to update age groups preference';
            showToast(errorMessage, 'error');
        }
    };

    const handleManageSubscription = () => {
        navigate('/admin/subscription');
    };

    const handleDeleteAccount = () => {
        setDeleteModalVisible(true);
    };

    const confirmDeleteAccount = () => {
        setDeleteModalVisible(false);
        showToast(
            'Account deletion feature coming soon. Please contact support.',
            'info'
        );
    };

    if (loading) {
        return (
            <div className="container main">
                <div style={{ textAlign: 'center', padding: '50px' }}>
                    <Spin size="large" />
                </div>
            </div>
        );
    }

    const isActive = subscriptionData?.subscription_status === 'active';

    return (
        <div className="container main">
            <h1>Settings</h1>

            <div style={{ maxWidth: '900px', margin: '0 auto' }}>
                {/* Profile Section */}
                <Card
                    title={
                        <span>
                            <UserOutlined /> Profile
                        </span>
                    }
                    style={{ marginBottom: '20px' }}
                    extra={
                        !isEditingProfile && (
                            <Button
                                type="link"
                                icon={<EditOutlined />}
                                onClick={handleEditProfile}
                            >
                                Edit
                            </Button>
                        )
                    }
                >
                    {!isEditingProfile ? (
                        <>
                            <div style={{ marginBottom: '16px' }}>
                                <strong>Name:</strong>
                                <div style={{ marginTop: '8px', fontSize: '16px' }}>
                                    {userName}
                                </div>
                            </div>

                            <div style={{ marginBottom: '16px' }}>
                                <strong>Email:</strong>
                                <div style={{ marginTop: '8px', fontSize: '16px' }}>
                                    {userEmail}
                                </div>
                            </div>

                            <Divider />

                            <Button
                                icon={<LockOutlined />}
                                onClick={openPasswordModal}
                            >
                                Change Password
                            </Button>
                            <p
                                style={{
                                    fontSize: '13px',
                                    color: '#666',
                                    marginTop: '8px',
                                }}
                            >
                                Update your account password
                            </p>
                        </>
                    ) : (
                        <Form
                            form={profileForm}
                            layout="vertical"
                            onFinish={handleSaveProfile}
                        >
                            <Form.Item
                                label="Name"
                                name="name"
                                rules={[
                                    {
                                        required: true,
                                        message: 'Please enter your name',
                                    },
                                ]}
                            >
                                <Input
                                    prefix={<UserOutlined />}
                                    placeholder="Enter your name"
                                />
                            </Form.Item>

                            <Form.Item
                                label="Email"
                                name="email"
                                rules={[
                                    {
                                        required: true,
                                        message: 'Please enter your email',
                                    },
                                    {
                                        type: 'email',
                                        message: 'Please enter a valid email',
                                    },
                                ]}
                            >
                                <Input
                                    prefix={<MailOutlined />}
                                    placeholder="Enter your email"
                                />
                            </Form.Item>

                            <Form.Item style={{ marginBottom: 0, marginTop: '24px' }}>
                                <div
                                    style={{
                                        display: 'flex',
                                        gap: '8px',
                                        justifyContent: 'flex-end',
                                    }}
                                >
                                    <Button onClick={handleCancelEdit}>
                                        Cancel
                                    </Button>
                                    <Button
                                        type="primary"
                                        htmlType="submit"
                                        loading={savingProfile}
                                    >
                                        Save Changes
                                    </Button>
                                </div>
                            </Form.Item>
                        </Form>
                    )}
                </Card>

                {/* Preferences Section */}
                <Card
                    title={
                        <span>
                            <SettingOutlined /> Preferences
                        </span>
                    }
                    style={{ marginBottom: '20px' }}
                >
                    <div style={{ marginBottom: '16px' }}>
                        <div
                            style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                            }}
                        >
                            <div>
                                <strong>Show Age Groups</strong>
                                <p
                                    style={{
                                        fontSize: '13px',
                                        color: '#666',
                                        marginTop: '4px',
                                        marginBottom: 0,
                                    }}
                                >
                                    Display age group badges and filters on session
                                    pages
                                </p>
                            </div>
                            <Switch
                                checked={auth.currentUser?.show_age_groups || false}
                                onChange={handleAgeGroupsToggle}
                            />
                        </div>
                    </div>
                </Card>

                {/* Subscription Section */}
                <Card
                    title={
                        <span>
                            <CrownOutlined /> Subscription
                        </span>
                    }
                    style={{ marginBottom: '20px' }}
                >
                    <div style={{ marginBottom: '16px' }}>
                        <strong>Current Plan:</strong>
                        <div style={{ marginTop: '8px' }}>
                            {isActive ? (
                                <Tag color="green" icon={<CrownOutlined />}>
                                    PREMIUM - Unlimited Events
                                </Tag>
                            ) : (
                                <Tag color="default">FREE - 1 Event Limit</Tag>
                            )}
                        </div>
                    </div>

                    <div style={{ marginBottom: '16px' }}>
                        <strong>Events Created:</strong>{' '}
                        {subscriptionData?.event_count || 0}
                        {!isActive && ' / 1'}
                    </div>

                    {isActive && subscriptionData?.subscription_expires_at && (
                        <div style={{ marginBottom: '16px' }}>
                            <strong>Renews:</strong>{' '}
                            {new Date(
                                subscriptionData.subscription_expires_at
                            ).toLocaleDateString()}
                        </div>
                    )}

                    <Divider />

                    <Button type="primary" onClick={handleManageSubscription}>
                        {isActive ? 'Manage Subscription' : 'Upgrade to Premium'}
                    </Button>
                </Card>

                {/* Data & Privacy Section */}
                <Card
                    title={
                        <span>
                            <DeleteOutlined /> Data & Privacy
                        </span>
                    }
                    style={{ marginBottom: '20px' }}
                >
                    <div>
                        <strong style={{ color: '#ff4d4f' }}>Danger Zone</strong>
                        <div style={{ marginTop: '12px' }}>
                            <Button
                                danger
                                icon={<DeleteOutlined />}
                                onClick={handleDeleteAccount}
                            >
                                Delete Account
                            </Button>
                            <p
                                style={{
                                    fontSize: '13px',
                                    color: '#666',
                                    marginTop: '8px',
                                }}
                            >
                                Permanently delete your account and all data
                            </p>
                        </div>
                    </div>
                </Card>

                {/* About Section */}
                <Card
                    title={
                        <span>
                            <InfoCircleOutlined /> About
                        </span>
                    }
                >
                    <div style={{ marginBottom: '12px' }}>
                        <strong>ReEvent</strong>
                        <div style={{ fontSize: '13px', color: '#666' }}>
                            Version 1.0
                        </div>
                    </div>

                    <div style={{ marginTop: '16px' }}>
                        <Link to="/terms" style={{ marginRight: '16px' }}>
                            Terms of Service
                        </Link>
                        <Link to="/privacy">Privacy Policy</Link>
                    </div>

                    <div
                        style={{
                            marginTop: '16px',
                            fontSize: '13px',
                            color: '#999',
                        }}
                    >
                        © {new Date().getFullYear()} ReEvent. All rights reserved.
                    </div>
                </Card>
            </div>

            {/* Delete Account Confirmation Modal */}
            <Modal
                title="Delete Account"
                open={deleteModalVisible}
                onOk={confirmDeleteAccount}
                onCancel={() => setDeleteModalVisible(false)}
                okText="Delete Account"
                okButtonProps={{ danger: true }}
            >
                <p>
                    <strong>Are you sure you want to delete your account?</strong>
                </p>
                <p>This action cannot be undone. All your data will be permanently deleted:</p>
                <ul>
                    <li>All events and sessions</li>
                    <li>All participants</li>
                    <li>All attendance records</li>
                    <li>Your subscription (if active)</li>
                </ul>
            </Modal>

            {/* Change Password Modal */}
            <Modal
                title="Change Password"
                open={passwordModalVisible}
                onCancel={() => {
                    setPasswordModalVisible(false);
                    passwordForm.resetFields();
                }}
                footer={null}
            >
                <Form
                    form={passwordForm}
                    layout="vertical"
                    onFinish={handleChangePassword}
                >
                    <Form.Item
                        label="Current Password"
                        name="currentPassword"
                        rules={[
                            {
                                required: true,
                                message: 'Please enter your current password',
                            },
                        ]}
                    >
                        <Input.Password
                            prefix={<LockOutlined />}
                            placeholder="Enter current password"
                        />
                    </Form.Item>

                    <Form.Item
                        label="New Password"
                        name="newPassword"
                        rules={[
                            {
                                required: true,
                                message: 'Please enter your new password',
                            },
                            {
                                min: 6,
                                message: 'Password must be at least 6 characters',
                            },
                        ]}
                    >
                        <Input.Password
                            prefix={<LockOutlined />}
                            placeholder="Enter new password"
                        />
                    </Form.Item>

                    <Form.Item
                        label="Confirm New Password"
                        name="confirmPassword"
                        dependencies={['newPassword']}
                        rules={[
                            {
                                required: true,
                                message: 'Please confirm your new password',
                            },
                            ({ getFieldValue }) => ({
                                validator(_, value) {
                                    if (!value || getFieldValue('newPassword') === value) {
                                        return Promise.resolve();
                                    }
                                    return Promise.reject(
                                        new Error('Passwords do not match')
                                    );
                                },
                            }),
                        ]}
                    >
                        <Input.Password
                            prefix={<LockOutlined />}
                            placeholder="Confirm new password"
                        />
                    </Form.Item>

                    <Form.Item style={{ marginBottom: 0, marginTop: '24px' }}>
                        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                            <Button
                                onClick={() => {
                                    setPasswordModalVisible(false);
                                    passwordForm.resetFields();
                                }}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="primary"
                                htmlType="submit"
                                loading={changingPassword}
                            >
                                Change Password
                            </Button>
                        </div>
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
}

export default Settings;
